import {
  ATTACHMENT_KIND_LABELS,
  ATTACHMENT_UPLOAD_WINDOW_MINUTES,
  VIA_MCP_LABEL,
  type AttachmentAccess,
  type AttachmentKind,
  type AttachmentSlot,
  type TicketEventEntry,
} from '@easyhelp/shared';
import { formatDateTime } from '../../components/ticket';
import { eventActor } from './event-actor';

/**
 * Testi dell'assenza dichiarata degli allegati (story #8, DEC-010 punto 4): per ogni stato la causa
 * nota, così chi lavora il ticket non confonde «non arrivato» con «non previsto» o «rifiutato».
 */

/** Etichette delle righe del contesto: distinte dalla «Schermata» segnalata (l'etichetta della pagina). */
export const ATTACHMENT_ROW_LABELS: Record<AttachmentKind, string> = {
  schermata: 'Schermata catturata',
  log: 'Log delle ultime azioni',
};

/** Comandi di apertura, su richiesta. */
export const OPEN_LABELS: Record<AttachmentKind, string> = {
  schermata: 'Mostra la schermata',
  log: 'Mostra il log',
};

/** Desinenza del participio concordato col genere del tipo: la schermata, il log. */
const ENDING: Record<AttachmentKind, 'a' | 'o'> = { schermata: 'a', log: 'o' };

/**
 * L'evento di un allegato nel thread (#120), con il tipo al posto di «Allegato»: «Schermata
 * ricevuta da EasyCare.», «Log delle ultime azioni ricevuto da EasyCare.». Il consumer carica al
 * più un allegato per tipo e il nome del file non è registrato: il tipo basta a distinguerli.
 * `null` se l'evento non è di un allegato o non ne ha il tipo (il testo resta quello generico).
 */
export function attachmentEventText(entry: TicketEventEntry): string | null {
  const kind = entry.attachmentKind;
  if (!kind) return null;
  const e = ENDING[kind];
  const label = ATTACHMENT_KIND_LABELS[kind];
  switch (entry.type) {
    case 'allegato_ricevuto':
      return `${label} ricevut${e} da ${eventActor(entry)}.`;
    case 'allegato_rifiutato':
      // Chi rifiuta è EasyHelp: il consumer è chi l'ha inviato.
      return `${label} inviat${e} da ${eventActor(entry)} e rifiutat${e}.`;
    case 'allegato_eliminato':
      return `${label} eliminat${e} per retention.`;
    default:
      return null;
  }
}

/**
 * Origine di un accesso nel registro (DEC-015 decisione 6, #155): niente per la console, «via MCP» con
 * il nome del client per una lettura da Claude o Cursor. È testo, non un'icona: si legge anche con lo
 * screen reader.
 */
export function accessOriginText({ viaMcp }: Pick<AttachmentAccess, 'viaMcp'>): string | null {
  if (!viaMcp) return null;
  return viaMcp.client ? `${VIA_MCP_LABEL} (${viaMcp.client})` : VIA_MCP_LABEL;
}

/**
 * Scioglimento della sigla sotto il registro, quando c'è almeno una lettura via MCP (WCAG 2.2, 3.1.4
 * Abbreviazioni, AAA): in testo visibile, quindi anche da tastiera e su touch. Il nome del client è
 * quello che il client dichiara al collegamento.
 */
export const VIA_MCP_NOTE =
  '«via MCP»: letto da un assistente (Claude o Cursor) collegato a EasyHelp con il Model Context Protocol, a nome della persona; il nome tra parentesi è quello che il client dichiara.';

/** Sul pre-accesso schermata e log non esistono per costruzione (contratto §5.3). */
export const NO_SESSION_TEXT: Record<AttachmentKind, string> = {
  schermata: 'Non prevista: segnalazione aperta senza accesso, nessuna sessione da cui catturarla.',
  log: 'Non previsto: segnalazione aperta senza accesso, nessuna sessione da cui raccoglierlo.',
};

const MEDIA_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WebP',
  'application/json': 'JSON',
};

const time = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });

/** Dimensione leggibile in unità binarie, come i limiti di DEC-010: «820 B», «12,5 kB», «1,2 MB». */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const format = (value: number) => value.toLocaleString('it-IT', { maximumFractionDigits: 1 });
  if (bytes < 1024 * 1024) return `${format(bytes / 1024)} kB`;
  return `${format(bytes / (1024 * 1024))} MB`;
}

/**
 * Lo stato di un tipo di allegato come frase con la causa nota. I rifiuti si elencano a parte
 * (`AttachmentSlot.rifiuti`): qui solo l'ultimo motivo.
 */
export function describeAttachmentSlot(
  kind: AttachmentKind,
  slot: AttachmentSlot,
  uploadDeadline: string | null
): string {
  const e = ENDING[kind];
  const { allegato } = slot;
  switch (slot.stato) {
    case 'custodito':
    case 'eliminato_per_retention': {
      if (!allegato) return `Non arrivat${e}.`;
      const facts = `${MEDIA_LABELS[allegato.mediaType] ?? allegato.mediaType} · ${formatBytes(allegato.dimensione)} · ricevut${e} il ${formatDateTime(allegato.ricevutoIl)}`;
      if (slot.stato === 'custodito' || !allegato.eliminatoIl) return facts;
      return `Eliminat${e} per retention il ${formatDateTime(allegato.eliminatoIl)}: restano solo i metadati (${facts}).`;
    }
    case 'rifiutato': {
      const last = slot.rifiuti.at(-1);
      return last ? `Rifiutat${e} al caricamento: ${last.motivo}` : `Rifiutat${e} al caricamento.`;
    }
    case 'in_attesa':
      return uploadDeadline
        ? `Non ancora arrivat${e}: il consumer può caricarl${e} fino alle ${time.format(new Date(uploadDeadline))}.`
        : `Non ancora arrivat${e}.`;
    case 'non_inviato':
      return `Non arrivat${e}: il consumer non l'ha caricat${e} entro ${ATTACHMENT_UPLOAD_WINDOW_MINUTES} minuti dalla ricezione (esclus${e} da chi ha segnalato o pers${e} nell'invio: non si distingue).`;
    case 'non_previsto':
      return NO_SESSION_TEXT[kind];
  }
}
